<script setup lang="ts">
/**
 * Цвет колонки — точка 12 из макета 24:74, по клику палитра.
 *
 * Не `InlinePicker`: цвет выбирают глазами, а списком строк с названиями
 * («Серый», «Синий») его пришлось бы называть словами — и переводить на три
 * языка то, что и так видно.
 */
import { onClickOutside, useEventListener } from '@vueuse/core'
import { ref } from 'vue'

import { COLUMN_COLORS } from '../api'

defineProps<{ modelValue: string; label: string }>()

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

const root = ref<HTMLElement | null>(null)
const isOpen = ref(false)

onClickOutside(root, () => {
  isOpen.value = false
})

useEventListener(document, 'keydown', (event: KeyboardEvent) => {
  if (event.key === 'Escape') isOpen.value = false
})

const pick = (color: string) => {
  emit('update:modelValue', color)
  isOpen.value = false
}
</script>

<template>
  <div ref="root" class="cpick">
    <button
      type="button"
      class="cpick__dot"
      :style="{ background: modelValue || 'var(--color-status-backlog)' }"
      :aria-label="label"
      :aria-expanded="isOpen"
      @click="isOpen = !isOpen"
    />

    <div v-if="isOpen" class="cpick__panel">
      <button
        v-for="color in COLUMN_COLORS"
        :key="color"
        type="button"
        class="cpick__option"
        :class="{ 'cpick__option--active': color === modelValue }"
        :style="{ background: color }"
        :aria-label="color"
        @click="pick(color)"
      />
    </div>
  </div>
</template>

<style scoped>
.cpick {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
}

.cpick__dot {
  width: 12px;
  height: 12px;
  padding: 0;
  border: none;
  border-radius: var(--radius-full);
  cursor: pointer;
}

.cpick__panel {
  position: absolute;
  top: 20px;
  left: -8px;
  z-index: 20;
  display: grid;
  grid-template-columns: repeat(4, 20px);
  gap: 8px;
  padding: 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  box-shadow: var(--shadow-md);
}

.cpick__option {
  width: 20px;
  height: 20px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: var(--radius-full);
  background-clip: padding-box;
  cursor: pointer;
}

.cpick__option--active {
  border-color: var(--color-text-primary);
}
</style>
